import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { UpsellDialogComponent } from 'app/shared/components/upsell/upsell-dialog.component';
import { catchError, throwError } from 'rxjs';

/**
 * What the server says when a plan is the reason.
 *
 * Two shapes reach here: 402 from the feature middleware, and a validation
 * error whose message came from a plan allowance. Both mean the same thing to
 * the person reading it, and neither should arrive as a red bar they have to
 * interpret.
 */
function planRefusal(error: HttpErrorResponse): string | null {
    if (error.status === 402) {
        const feature = error.error?.feature;

        // Named, because "this feature" is not a thing anybody can picture.
        return feature
            ? `${feature}: ${error.error?.message ?? ''}`.trim()
            : (error.error?.message ?? null);
    }

    if (error.status === 422) {
        const messages: string[] = Object.values(error.error?.errors ?? {}).flat() as string[];
        const hit = messages.find((m) => typeof m === 'string' && m.startsWith('Your plan allows'));

        return hit ?? null;
    }

    return null;
}

/**
 * Reads a cookie value by name. A simple helper function.
 */
function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[2]) : null;
}

/**
 * This is a functional HTTP interceptor that handles two primary responsibilities
 * for the entire application's network requests.
 *
 * 1.  **Credential Management**: It ensures that browser cookies (like the session cookie)
 *     are sent with every request by setting `withCredentials: true`.
 *
 * 2.  **CSRF Protection**: It reads the `XSRF-TOKEN` from the document's cookies and
 *     adds it to the `X-XSRF-TOKEN` header, which is the standard mechanism for
 *     preventing Cross-Site Request Forgery attacks.
 *
 * 3.  **Global Auth Error Handling**: It catches any `401 Unauthorized` or `403 Forbidden`
 *     responses from the API. These statuses indicate an invalid or expired session.
 *     When this happens, it automatically clears any local user data and redirects
 *     the user to the sign-in page, ensuring the application remains in a secure state.
 */
export const credentialsInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const auth = inject(AuthService);
    const dialog = inject(MatDialog);
    const xsrfToken = getCookie('XSRF-TOKEN');

    // Clone the request to add the withCredentials flag and the CSRF token header.
    const clonedReq = req.clone({
        withCredentials: true,
        setHeaders: xsrfToken ? { 'X-XSRF-TOKEN': xsrfToken } : {},
    });

    return next(clonedReq).pipe(
        catchError((error) => {
            // 401 means the session is gone. Leaving it to the guard does not
            // work: by the time a screen's own request comes back 401 the
            // guard has already said yes and the navigation is under way, so
            // the error cancels it in silence -- which is a button that does
            // nothing until you press it again.
            //
            // 403 is deliberately not treated this way. It means signed in but
            // not allowed, and signing somebody out over a permission they do
            // not have would throw away a working session.
            if (error instanceof HttpErrorResponse && error.status === 401) {
                localStorage.removeItem('currentUser');

                // So the next guard asks the server instead of reusing a yes
                // from before the session expired.
                auth.invalidate();

                // Not for the sign-in attempt itself, which answers 401 for a
                // wrong password and must stay on the page to say so.
                const isAuthCall = /\/(login|sanctum|resetPassword)/.test(req.url);

                if (!isAuthCall && !router.url.startsWith('/sign-in')) {
                    router.navigate(['/sign-in'], {
                        queryParams: { redirectURL: router.url },
                    });
                }
            }

            // A refusal that is really an offer. Explained, with the next
            // plan and its price on the button, rather than left as an error
            // the reader has to work out for themselves.
            if (error instanceof HttpErrorResponse) {
                const refusal = planRefusal(error);

                // Only for something the reader just did. A screen loading
                // data it is not entitled to should render without that part,
                // not throw a dialog at somebody who has pressed nothing —
                // which is what reading the People list did.
                const asked = req.method !== 'GET';

                if (refusal && asked) {
                    dialog.open(UpsellDialogComponent, {
                        width: '520px',
                        maxWidth: '92vw',
                        autoFocus: false,
                        data: { title: refusal, reason: null },
                    });
                }
            }

            // Always re-throw the error to allow local error handlers (in services, guards, etc.) to catch it.
            return throwError(() => error);
        })
    );
};
